import baseApi from "./RootBaseApi";
import {
  Article,
  ArticleVersion,
  TranslateArticleRequest,
  TranslateArticleResponse,
} from "@/types/cms.types";

export const ArticleService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getArticles: builder.query<
      {
        data: Article[];
        totalItems: number;
        totalPages: number;
        page: number;
        limit: number;
      },
      { page?: number; limit?: number }
    >({
      query: (params) => ({
        url: "/articles",
        method: "GET",
        params,
      }),
      providesTags: ["Articles"],
    }),
    getArticleById: builder.query<Article, string>({
      query: (id) => ({
        url: `/articles/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Articles", id }],
    }),
    createArticle: builder.mutation<Article, FormData>({
      query: (body) => ({
        url: "/articles",
        method: "POST",
        data: body,
      }),
      invalidatesTags: ["Articles"],
    }),
    updateArticle: builder.mutation<Article, { id: string; body: FormData }>({
      query: ({ id, body }) => ({
        url: `/articles/${id}`,
        method: "PATCH",
        data: body,
      }),
      invalidatesTags: (result, error, { id }) => [
        "Articles",
        { type: "Articles", id },
      ],
    }),
    deleteArticle: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/articles/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Articles"],
    }),
    updateArticleStatus: builder.mutation<
      Article,
      { id: string; status: string; publishedAt?: string }
    >({
      query: ({ id, status, publishedAt }) => ({
        url: `/articles/${id}/status`,
        method: "PATCH",
        data: publishedAt ? { status, publishedAt } : { status },
      }),
      invalidatesTags: (result, error, { id }) => [
        "Articles",
        { type: "Articles", id },
      ],
    }),
    translateArticle: builder.mutation<
      TranslateArticleResponse,
      TranslateArticleRequest
    >({
      query: (body) => ({
        url: "/articles/translate",
        method: "POST", // User said GET in request body description, but usually translate with body is POST. I'll use POST as it's more standard for bodies.
        data: body,
      }),
    }),
    uploadImage: builder.mutation<{ url: string }, FormData>({
      query: (body) => ({
        url: "/articles/upload-image",
        method: "POST",
        data: body,
      }),
    }),
    getArticleVersions: builder.query<{ data: ArticleVersion[] }, string>({
      query: (id) => ({
        url: `/articles/${id}/versions`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [
        { type: "Articles", id: `${id}-versions` },
      ],
    }),
    publishArticleVersion: builder.mutation<
      Article,
      { id: string; version: number }
    >({
      query: ({ id, version }) => ({
        url: `/articles/${id}/versions/publish`,
        method: "PATCH",
        data: { version },
      }),
      invalidatesTags: (result, error, { id }) => [
        "Articles",
        { type: "Articles", id },
        { type: "Articles", id: `${id}-versions` },
      ],
    }),
  }),
});

export const {
  useGetArticlesQuery,
  useGetArticleByIdQuery,
  useCreateArticleMutation,
  useUpdateArticleMutation,
  useDeleteArticleMutation,
  useUpdateArticleStatusMutation,
  useTranslateArticleMutation,
  useUploadImageMutation,
  useGetArticleVersionsQuery,
  usePublishArticleVersionMutation,
} = ArticleService;
